/**
 * ProductCategoryQuickCreateDialog - Minimal dialog for quick product category creation.
 *
 * Following the Quick Create Dialog pattern (3-5 fields → detail page):
 * - Fields: Name (required), Description (optional), Parent category (optional)
 * - When opened from a ProductCategoryPicker, pass `skipNavigation` + `onCreated`
 *   so the user stays in the parent form and the new category is auto-selected.
 */
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { Input } from '../../../../components/ui/input';
import { Label } from '../../../../components/ui/label';
import { Textarea } from '../../../../components/ui/textarea';
import { DialogForm } from '../../../../shared/components/common';
import { useEmbeddingPrefix } from '../../../../shared/hooks';
import { useCreateProductCategory } from '../hooks/useProductCategories';
import { ProductCategoryPicker } from './ProductCategoryPicker';
import type { ProductCategory } from '../types';

interface ProductCategoryQuickCreateDialogProps {
  open: boolean;
  onClose: () => void;
  /** Callback when the category is created (before navigation). */
  onCreated?: (category: ProductCategory) => void;
  /** If true, don't navigate to the detail page after creation. */
  skipNavigation?: boolean;
  /** Pre-fill the name field (e.g. from the picker's search text). */
  defaultName?: string;
  /** Pre-select a parent category (e.g. creating a child from the tree). */
  defaultParentId?: string | null;
}

interface QuickCreateFormData {
  name: string;
  description: string;
}

export function ProductCategoryQuickCreateDialog({
  open,
  onClose,
  onCreated,
  skipNavigation = false,
  defaultName = '',
  defaultParentId = null,
}: ProductCategoryQuickCreateDialogProps) {
  const { t } = useTranslation(['products', 'common']);
  const navigate = useNavigate();
  const { prefix } = useEmbeddingPrefix();
  const createMutation = useCreateProductCategory();
  const [parentId, setParentId] = useState<string>(defaultParentId ?? '');

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<QuickCreateFormData>({
    mode: 'onBlur',
    defaultValues: { name: defaultName, description: '' },
  });

  useEffect(() => {
    if (open) {
      reset({ name: defaultName, description: '' });
      setParentId(defaultParentId ?? '');
    }
  }, [open, reset, defaultName, defaultParentId]);

  const onSubmit = async (data: QuickCreateFormData) => {
    try {
      const category = await createMutation.mutateAsync({
        name: data.name.trim(),
        description: data.description.trim() || undefined,
        parent_id: parentId || undefined,
      });
      onCreated?.(category);
      onClose();
      if (!skipNavigation) {
        navigate(`${prefix}/categories/${category.id}`);
      }
    } catch {
      // The global mutation-error handler shows the specific translated toast.
    }
  };

  return (
    <DialogForm
      open={open}
      onClose={onClose}
      title={t('categories.create')}
      onSubmit={handleSubmit(onSubmit)}
      submitLabel={t('common:app.create')}
      isLoading={isSubmitting}
      maxWidth="sm"
    >
      <div className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="category_name" required>{t('categories.name')}</Label>
          <Controller
            name="name"
            control={control}
            rules={{ required: t('common:validation.required') }}
            render={({ field }) => (
              <Input
                {...field}
                id="category_name"
                maxLength={120}
                placeholder={t('categories.namePlaceholder')}
                autoFocus
              />
            )}
          />
          {errors.name && (
            <p className="text-sm text-destructive">{errors.name.message}</p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="category_parent">{t('categories.parent')}</Label>
          <ProductCategoryPicker
            value={parentId}
            onChange={setParentId}
            placeholder={t('categories.noParent')}
            hideAddNew
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="category_description">{t('categories.description')}</Label>
          <Controller
            name="description"
            control={control}
            render={({ field }) => (
              <Textarea
                {...field}
                id="category_description"
                rows={2}
                maxLength={500}
                placeholder={t('categories.descriptionPlaceholder')}
              />
            )}
          />
        </div>
      </div>
    </DialogForm>
  );
}
